/* eslint-disable no-nested-ternary */
import React, { useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { theme, Spinner } from 'fpay-ui';
import { useSelector } from 'react-redux';

import { getFormatedMoneyGreen } from '@helpers/andesUtils';
import { useAdvancements } from '@hooks';

const fontTypes = {
  title: css`
    ${({ theme }) => theme.textTypes['Title-s22-w700']};
    color: ${({ theme }) => theme.colors.textBlack};
    ${({ theme }) => theme.newBreakpoint('sm')} {
      ${({ theme }) => theme.textTypes['Title-s18-w700']};
    }
  `,
  subtitle: css`
    ${({ theme }) => theme.textTypes['Body-3-w400']};
    color: ${({ theme }) => theme.colors.textBlack};
  `,
  button: css`
    ${({ theme }) => theme.textTypes['Body-2-w400']};
    line-height: normal;
  `,
  amount: css`
    ${({ theme }) => theme.textTypes['Subtitle-1-w400']};
    line-height: normal;
  `,
};

const Title = styled.div`
  ${fontTypes.title}
  margin-bottom: 8px;
  &.warning {
    margin-bottom: 20px;
  }
`;

const Subtitle = styled.div`
  ${fontTypes.subtitle}
  margin-bottom: 24px;
  &.warning {
    margin-bottom: 20px;
  }
`;

const Layout = styled.div`
  padding: 40px 32px 32px 32px;
  ${({ theme }) => theme.newBreakpoint('sm')} {
    padding: 40px 24px 16px 24px;
  }
`;

const LayoutAmounts = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const LayoutAdvice = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LayoutButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-top: 40px;
  gap: 8px;
  ${({ theme }) => theme.newBreakpoint('sm')} {
    flex-direction: column-reverse;
  }
`;

const AmountLabel = styled.div`
  ${({ theme }) => css`
    ${fontTypes.amount}
    color: ${theme.colors.gray800};
    font-weight: normal;
    text-align: right;
    &.leftLabel {
      color: ${theme.colors.gray700};
      margin: 0;
      text-align: left;
    }
    &.totalLabel {
      font-weight: bold;
      margin-bottom: 24px;
    }
  `}
`;

const TotalBorder = styled(Box)`
  ${() => css`
    width: 440px;
    height: 144px;
    border-radius: 8px;
    box-shadow: 0 4px 22px 0 ${({ theme }) => theme.colors.shadowBlack};
    padding: 24px;
    ${({ theme }) => theme.newBreakpoint('sm')} {
      width: 280px;
      height: 144px;
      padding: 24px 16px 24px 16px;
    }
    .secondRow {
      margin-top: 4px;
    }
    .divider {
      margin-top: 16px;
      margin-bottom: 12px;
    }
  `}
`;

const Row = styled(Box)`
  ${() => css`
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    box-sizing: border-box;
  `}
`;

const HR = styled(Box)`
  ${() => css`
    width: 392px;
    border: 1px solid ${({ theme }) => theme.colors.dividerGray};
    ${({ theme }) => theme.newBreakpoint('sm')} {
      width: 248px;
    }
  `}
`;

const AdviceText = styled.div`
  ${() => css`
    color: ${({ theme }) => theme.colors.textBlack};
    ${({ theme }) => theme.textTypes['Body-3-w400']};
    text-align: center;
  `}
`;

const StyledSpinner = styled.div`
  padding-bottom: 24px;
`;

const Button = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: center;
    ${fontTypes.button}
    text-align: center;
    align-items: center;
    width: 216px;
    height: 48px;
    border-radius: 4px;
    background-color: ${(props) => (props.noThanks ? '#ffffff' : `${theme.colors.gbrand500}`)};
    color: ${(props) => (props.noThanks ? `${theme.colors.gbrand500}` : '#ffffff')};
    cursor: pointer;
    transition: all 0.3s ease 0s;
    &:hover {
      box-shadow: ${(props) => (props.noThanks ? '' : '0 1px 6px rgb(32 33 36 / 28%)')};
      background-color: ${(props) => (props.noThanks ? '' : `${theme.colors.gbrand600}`)};
      color: ${(props) => (props.noThanks ? `${theme.colors.gbrand600}` : '#ffffff')};
    }
    &:active {
      border: ${(props) => (props.noThanks ? '' : `1px solid ${theme.colors.gbrand500}`)};
    }
  `
);

const SimulationAdvancement = ({ handleCancel, liquidationAmount }) => {
  const { t } = useTranslation();
  const showAdvancementsLoading = useSelector(
    (state) => state.advancements.showAdvancementsLoading
  );
  const {
    advancementSimulation,
    doGetAdvancementSimulation,
    canExecuteAdvancement,
    executeAdvancement,
  } = useAdvancements();

  const { advanceableIncome } = useSelector((state) => state.andes);

  useEffect(() => doGetAdvancementSimulation(), [doGetAdvancementSimulation]);

  const confirm = async () => {
    await executeAdvancement(liquidationAmount);
  };

  const percentageFormatter = (num) =>
    new Intl.NumberFormat('default', {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num);
  return (
    <Layout>
      <Title>{t('homeScreen.acelerationBanner.title')}</Title>
      <Subtitle>
        {advancementSimulation !== null &&
          t('homeScreen.acelerationBanner.subtitle', {
            tax: percentageFormatter(advancementSimulation.tax.rav.tax),
          })}
      </Subtitle>
      <LayoutAmounts>
        <TotalBorder>
          {showAdvancementsLoading || !advancementSimulation ? (
            <StyledSpinner>
              <Spinner />
            </StyledSpinner>
          ) : advancementSimulation.totals.max_advancement_amount > 0 ||
            (advanceableIncome <= 0 &&
              advancementSimulation.totals.max_advancement_amount === 0) ? (
            <>
              <Row className="firstRow">
                <Grid item xs={7}>
                  <AmountLabel className="leftLabel">
                    {t('homeScreen.acelerationBanner.subTotal')}
                  </AmountLabel>
                </Grid>
                <Grid item xs={5}>
                  <AmountLabel>
                    {getFormatedMoneyGreen(
                      advancementSimulation.totals.max_advancement_amount,
                      theme.colors.gray800
                    )}
                  </AmountLabel>
                </Grid>
              </Row>
              <Row className="secondRow">
                <Grid item xs={7}>
                  <AmountLabel className="leftLabel">
                    {t('homeScreen.acelerationBanner.discount')}
                  </AmountLabel>
                </Grid>
                <Grid item xs={5}>
                  <AmountLabel>
                    {getFormatedMoneyGreen(
                      advancementSimulation.totals.advancement_discount_amount,
                      theme.colors.gray800
                    )}
                  </AmountLabel>
                </Grid>
              </Row>
              <HR className="divider" />
              <Row className="thirdRow">
                <Grid item xs={7}>
                  <AmountLabel className="leftLabel totalLabel">
                    {t('homeScreen.acelerationBanner.total')}
                  </AmountLabel>
                </Grid>
                <Grid item xs={5}>
                  <AmountLabel className="totalLabel">
                    {getFormatedMoneyGreen(
                      advancementSimulation.totals.advancement_amount,
                      theme.colors.gray800
                    )}
                  </AmountLabel>
                </Grid>
              </Row>
            </>
          ) : (
            <Title className="warning">{t('homeScreen.acelerationBanner.disabled')}</Title>
          )}
        </TotalBorder>
        <LayoutAdvice>
          <AdviceText>{t('homeScreen.acelerationBanner.advice')}</AdviceText>
        </LayoutAdvice>
      </LayoutAmounts>
      <LayoutButtons>
        <Button
          noThanks
          id={canExecuteAdvancement() ? 'advance-close-btn' : 'advance-close-not-enabled-btn'}
          onClick={handleCancel}
        >
          {t('homeScreen.acelerationBanner.cancel')}
        </Button>
        {canExecuteAdvancement() && (
          <Button id="advance-confirm-btn" style={{ border: 'none' }} onClick={confirm}>
            {t('homeScreen.acelerationBanner.confirm')}
          </Button>
        )}
      </LayoutButtons>
    </Layout>
  );
};

export default SimulationAdvancement;
